import { useEffect, useState } from "react";
import axios from "axios";
import { ExternalLink, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import AnimatedSection from "@/components/AnimatedSection";
import { getFileUrl } from "@/components/FileUpload";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ShopButton({ url, label, testId, glow = false, productId = "", productName = "" }) {
  const handleClick = () => {
    track("affiliate_click", {
      target_id: productId || url,
      target_type: "affiliate",
      target_label: productName || label,
      meta: { href: url },
    });
  };
  return (
    <a
      href={url || "#"}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      data-testid={testId}
      onClick={handleClick}
      className={`group inline-flex items-center justify-center gap-2 w-full h-11 px-5 text-xs uppercase tracking-[0.2em] font-semibold bg-[#D4AF37] text-black rounded-md transition-all duration-300 hover:bg-white hover:shadow-[0_0_24px_rgba(212,175,55,0.55)] ${
        glow ? "shadow-[0_0_18px_rgba(212,175,55,0.35)]" : ""
      }`}
    >
      {label || "SHOP NOW"}
      <ExternalLink className="w-3.5 h-3.5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
    </a>
  );
}

function ProductCard({ product, testId }) {
  const img = product.image_url ? getFileUrl(product.image_url) : "";
  return (
    <div
      data-testid={testId}
      className="group relative rounded-2xl bg-[#0B0B0B] border border-white/5 overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:border-[#D4AF37]/40 hover:shadow-[0_20px_60px_-20px_rgba(212,175,55,0.4)]"
    >
      <div className="aspect-square overflow-hidden bg-[#0A0A0A]">
        {img ? (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/10">
            <Sparkles className="w-10 h-10" />
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col gap-3">
        <div>
          <h3 className="font-heading text-xl tracking-wide text-white line-clamp-1">
            {product.name}
          </h3>
          {product.description && (
            <p className="text-white/45 text-sm mt-1.5 leading-relaxed line-clamp-2">
              {product.description}
            </p>
          )}
        </div>
        {product.price ? (
          <p className="text-[#D4AF37] font-heading text-lg tracking-wider">
            {String(product.price).startsWith("$") ? product.price : `$${product.price}`}
          </p>
        ) : null}
        <ShopButton
          url={product.affiliate_url}
          label={product.button_label}
          testId={`affiliate-shop-btn-${product.id}`}
          productId={product.id}
          productName={product.name}
        />
      </div>
    </div>
  );
}

function FeaturedCard({ featured }) {
  if (!featured?.enabled) return null;
  const img = featured.image_url ? getFileUrl(featured.image_url) : "";
  return (
    <AnimatedSection testId="affiliate-featured">
      <div className="relative max-w-4xl mx-auto rounded-3xl overflow-hidden border border-[#D4AF37]/30 bg-gradient-to-br from-[#0E0E0E] via-[#0A0A0A] to-[#0E0E0E] shadow-[0_30px_120px_-30px_rgba(212,175,55,0.45)]">
        {/* Gold glow halo */}
        <div className="pointer-events-none absolute -inset-1 bg-[radial-gradient(circle_at_50%_0%,rgba(212,175,55,0.18),transparent_60%)]" />
        <div className="grid md:grid-cols-2 gap-0 relative">
          <div className="aspect-square md:aspect-auto bg-black overflow-hidden">
            {img ? (
              <img
                src={img}
                alt={featured.name}
                className="w-full h-full object-cover transition-transform duration-1000 ease-out hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/10">
                <Sparkles className="w-16 h-16" />
              </div>
            )}
          </div>
          <div className="p-8 md:p-12 flex flex-col justify-center gap-5">
            <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.4em] font-semibold flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Featured
            </p>
            <h3 className="font-heading text-4xl md:text-5xl leading-[0.95]">
              {featured.name || "—"}
            </h3>
            {featured.description && (
              <p className="text-white/65 text-base leading-relaxed">{featured.description}</p>
            )}
            {featured.price ? (
              <p className="text-[#D4AF37] font-heading text-2xl tracking-wider">
                {String(featured.price).startsWith("$") ? featured.price : `$${featured.price}`}
              </p>
            ) : null}
            <div className="pt-2 max-w-xs">
              <ShopButton
                url={featured.affiliate_url}
                label={featured.button_label}
                testId="affiliate-featured-shop-btn"
                productId={"featured"}
                productName={featured.name}
                glow
              />
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
}

function Section({ section, products }) {
  const items = products.filter((p) => p.section_key === section.key);
  if (items.length === 0) return null;
  return (
    <section data-testid={`affiliate-section-${section.key}`} className="mb-20">
      <div className="flex items-end justify-between gap-6 mb-8">
        <div>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-wide">{section.title}</h2>
          {section.description && (
            <p className="text-white/40 text-sm mt-2 max-w-xl">{section.description}</p>
          )}
        </div>
        <div className="flex-1 hidden sm:block h-[1px] bg-gradient-to-r from-[#D4AF37]/30 via-white/5 to-transparent mb-3" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((p, i) => (
          <AnimatedSection key={p.id} delay={i * 60}>
            <ProductCard product={p} testId={`affiliate-product-${p.id}`} />
          </AnimatedSection>
        ))}
      </div>
    </section>
  );
}

export default function Affiliate() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/affiliate/config?_t=${Date.now()}`)
      .then((r) => {
        setConfig(r.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading || !config) {
    return (
      <div className="pt-24 min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { hero, featured, sections = [], cta, footer_disclaimer, products = [] } = config;
  const sortedSections = [...sections].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div data-testid="affiliate-page" className="pt-16 bg-[#050505]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A] via-[#070707] to-[#050505]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-10%,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="absolute inset-0 opacity-[0.04] mix-blend-overlay"
             style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }}
        />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />

        <div className="relative max-w-6xl mx-auto px-6 py-28 sm:py-36 text-center">
          <p className="text-[#D4AF37] text-[11px] uppercase tracking-[0.5em] font-semibold mb-5">
            Affiliate · Curated
          </p>
          <h1
            data-testid="affiliate-hero-title"
            className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-wide"
          >
            {hero?.title}
          </h1>
          {hero?.subtitle && (
            <p className="mt-5 text-white/80 text-lg sm:text-xl tracking-[0.15em] uppercase">
              {hero.subtitle}
            </p>
          )}
          {hero?.supporting && (
            <p className="mt-3 text-white/45 text-sm sm:text-base italic max-w-xl mx-auto">
              {hero.supporting}
            </p>
          )}
          {hero?.disclaimer && (
            <p className="mt-10 text-white/30 text-[11px] uppercase tracking-wider">
              {hero.disclaimer}
            </p>
          )}
        </div>
      </section>

      {/* FEATURED + SECTIONS */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {featured?.enabled && (
          <div className="mb-24">
            <FeaturedCard featured={featured} />
          </div>
        )}

        {sortedSections.map((section) => (
          <Section key={section.key} section={section} products={products} />
        ))}

        {products.length === 0 && !featured?.enabled && (
          <div className="text-center py-20 text-white/30">
            <Sparkles className="w-10 h-10 mx-auto mb-4 opacity-40" />
            <p className="text-lg">The collection is being curated.</p>
            <p className="text-sm mt-2 text-white/20">Check back soon for handpicked drops.</p>
          </div>
        )}
      </div>

      {/* CTA */}
      {(cta?.title || cta?.subtext) && (
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505] via-[#0A0A0A] to-[#050505]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,0,0,0.15),transparent_60%)]" />
          <div className="relative max-w-3xl mx-auto px-6 py-24 text-center">
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-wide">
              {cta.title}
            </h2>
            {cta.subtext && (
              <p className="mt-5 text-white/55 text-base sm:text-lg max-w-xl mx-auto">
                {cta.subtext}
              </p>
            )}
            {cta.button_label && cta.button_url && (
              <div className="mt-10 inline-block">
                <a
                  href={cta.button_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow sponsored"
                  data-testid="affiliate-cta-btn"
                  className="group inline-flex items-center gap-2 px-9 h-12 text-xs uppercase tracking-[0.3em] font-semibold border border-[#D4AF37] text-[#D4AF37] rounded-md transition-all duration-300 hover:bg-[#D4AF37] hover:text-black hover:shadow-[0_0_30px_rgba(212,175,55,0.55)]"
                >
                  {cta.button_label}
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* FOOTER DISCLAIMER */}
      {footer_disclaimer && (
        <p className="text-center text-white/25 text-[11px] uppercase tracking-wider py-8 border-t border-white/5">
          {footer_disclaimer}
        </p>
      )}
    </div>
  );
}
